import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  EMAIL_NOT_VERIFIED_ERR,
  signInValidationSchema,
} from "../../../constants/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useResendVerificationEmail } from "../../../hooks/useResendVerificationEmail";
import { enqueueSnackbar } from "notistack";
import {
  WRONG_AUTH_CREDENTIALS,
  EMAIL_NOT_VERIFIED,
  RESEND_VERIFICATION_EMAIL_FAILED,
  RESEND_VERIFICATION_EMAIL_SUCCEEDED,
} from "../../../constants";
import { login } from "../../../services/apiV2";
import { axiosErrorMatches } from "../../../helpers";
import { useAppDispatch } from "../../../store/hooks";
import { setTokens } from "../../../features/user/user.slice";
import { getAmazonCredentialsThunk } from "../../../features/user/user.thunk";
import { syncExtensionAuth } from "../../../utils/extension";

type TFormFields = {
  email: string;
  password: string;
};

export type TErrors = {
  wrongCredentials: string | null;
  emailNotVerified: string | null;
  resendVerificationEmailFailed: string | null;
};

const ERRORS_INITIAL_STATE: TErrors = {
  wrongCredentials: null,
  emailNotVerified: null,
  resendVerificationEmailFailed: null,
};

export const useSignIn = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [errors, setErrors] = useState<TErrors>(ERRORS_INITIAL_STATE);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    getValues,
  } = useForm<TFormFields>({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: zodResolver(signInValidationSchema),
    reValidateMode: "onSubmit",
  });

  const navigate = useNavigate();

  const { resend, isLoading: isResendingVerificationEmail } =
    useResendVerificationEmail({
      onSuccess: () => {
        enqueueSnackbar(RESEND_VERIFICATION_EMAIL_SUCCEEDED, {
          variant: "success",
        });
        setErrors((prev) => ({
          ...prev,
          resendVerificationEmailFailed: null,
        }));
      },
      onError: () => {
        setErrors((prev) => ({
          ...prev,
          resendVerificationEmailFailed: RESEND_VERIFICATION_EMAIL_FAILED,
        }));
      },
    });

  const resendVerificationEmail = async () => {
    await resend(getValues("email"));
  };

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setErrors(ERRORS_INITIAL_STATE);

    try {
      const res = await login({ email, password });
      dispatch(setTokens(res.data));
      dispatch(getAmazonCredentialsThunk());
      const shouldClose = searchParams.get("from") === "extension";
      syncExtensionAuth(shouldClose);
      if (shouldClose) {
        window.close();
      } else {
        navigate("/", { replace: true });
      }
    } catch (e) {
      if (axiosErrorMatches(e, EMAIL_NOT_VERIFIED_ERR)) {
        setErrors((prev) => ({
          ...prev,
          emailNotVerified: EMAIL_NOT_VERIFIED,
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          wrongCredentials: WRONG_AUTH_CREDENTIALS,
        }));
      }
    }
  });

  return {
    errors,
    control,
    isSubmitting,
    isResendingVerificationEmail,
    onSubmit,
    resendVerificationEmail,
  };
};
